import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Provider, useDispatch, useSelector } from 'react-redux';
import { AppDispatch } from './store';
import { PersistGate } from 'redux-persist/integration/react';
import { ThemeProvider } from '@mui/material/styles';
import { CssBaseline, Box, Typography } from '@mui/material';
import { Toaster } from 'react-hot-toast';
import { HelmetProvider } from 'react-helmet-async';
import { ErrorBoundary } from 'react-error-boundary';
import { School } from '@mui/icons-material';

import { store, persistor } from './store';
import theme from './theme';
import LoginPage from './features/auth/LoginPage';
import WelcomePage from './features/welcome/WelcomePage';
import ProtectedRoute from './components/auth/ProtectedRoute';
import { setToken, getCurrentUser, clearAuth } from './store/slices/authSlice';
import authService from './services/authService';
import { RootState } from './store';

// Error Fallback Component
const ErrorFallback = ({ error, resetErrorBoundary }: any) => {
  return (
    <div style={{ padding: '20px', textAlign: 'center' }}>
      <h2>Something went wrong!</h2>
      <pre style={{ color: 'red' }}>{error.message}</pre>
      <button onClick={resetErrorBoundary}>Try again</button>
    </div>
  );
};

// App Content Component with token initialization
const AppContent = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { isAuthenticated, isLoading } = useSelector((state: RootState) => state.auth);

  useEffect(() => {
    const initializeAuth = async () => {
      const token = localStorage.getItem('token');
      
      if (token) {
        // Set token in auth service
        authService.setToken(token);
        dispatch(setToken(token));
        
        try {
          // Validate token by fetching current user
          // This ensures the token is still valid and user data is restored
          await dispatch(getCurrentUser());
        } catch (error) {
          // If token is invalid, clear authentication
          console.error('Token validation failed:', error);
          dispatch(clearAuth());
          authService.removeToken();
        }
      }
    };

    initializeAuth();
  }, [dispatch]);

  // Show loading while initializing authentication
  if (isLoading && !isAuthenticated) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 50%, ${theme.palette.primary.light} 100%)`,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 2,
          }}
        >
          <School
            sx={{
              fontSize: 48,
              color: 'white',
              mr: 1,
            }}
          />
        </Box>
        <Typography variant="h6" sx={{ color: 'white', mb: 1 }}>
          LiveOlympiad LMS
        </Typography>
        <Typography variant="body2" sx={{ color: 'white', opacity: 0.8 }}>
          Initializing...
        </Typography>
      </Box>
    );
  }

  return (
    <Router>
      <Routes>
        {/* Public Routes */}
        <Route path="/login" element={<LoginPage />} />

        {/* Protected Routes */}
        <Route
          path="/welcome"
          element={
            <ProtectedRoute>
              <WelcomePage />
            </ProtectedRoute>
          }
        />

        {/* Default redirect */}
        <Route path="/" element={<Navigate to="/welcome" replace />} />
        <Route path="*" element={<Navigate to="/welcome" replace />} />
      </Routes>
    </Router>
  );
};

function App() {
  return (
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      <Provider store={store}>
        <PersistGate loading={<div>Loading...</div>} persistor={persistor}>
          <ThemeProvider theme={theme}>
            <HelmetProvider>
              <CssBaseline />
              <AppContent />

              {/* Toast Notifications */}
              <Toaster
                position="top-right"
                toastOptions={{
                  duration: 4000,
                  style: {
                    background: '#363636',
                    color: '#fff',
                  },
                }}
              />
            </HelmetProvider>
          </ThemeProvider>
        </PersistGate>
      </Provider>
    </ErrorBoundary>
  );
}

export default App;
